import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, FileText, Truck, Users, PackageCheck, ArrowRight, ArrowUp, ArrowDown, Minus, MapPin, Clock, RefreshCw, Navigation, Warehouse, TrendingUp, Euro } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import axios from "axios";
import { setCache } from "@/utils/dataCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("mv_admin_token");
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const PAGE_SIZE = 50;

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Warm the cache for the two most-visited admin sections so the sidebar
// clicks feel instant. Called on Dashboard mount + refresh.
function prefetchAdminLists() {
  const h = authHeaders();
  const month = currentMonthKey();
  const jobs = [
    { key: `${API}/admin/shipments?month=${month}&limit=${PAGE_SIZE}`, url: `${API}/admin/shipments?month=${month}&limit=${PAGE_SIZE}` },
    { key: `${API}/admin/pickups?month=${month}&limit=${PAGE_SIZE}`, url: `${API}/admin/pickups?month=${month}&limit=${PAGE_SIZE}` },
  ];
  jobs.forEach(({ key, url }) => {
    axios.get(url, h).then((r) => {
      const total = parseInt(r.headers["x-total-count"] || String(r.data.length), 10);
      setCache(key, r.data, total);
    }).catch(() => {});
  });
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  return `${Math.floor(diff / 86400)} giorni fa`;
}

function KpiChange({ current, previous }) {
  if (previous === 0 && current === 0) return <span className="text-xs text-zinc-400 flex items-center gap-0.5"><Minus size={12} /> --</span>;
  if (previous === 0) return <span className="text-xs text-green-600 flex items-center gap-0.5"><ArrowUp size={12} /> Nuovo</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return <span className="text-xs text-green-600 flex items-center gap-0.5"><ArrowUp size={12} /> +{pct}%</span>;
  if (pct < 0) return <span className="text-xs text-red-500 flex items-center gap-0.5"><ArrowDown size={12} /> {pct}%</span>;
  return <span className="text-xs text-zinc-400 flex items-center gap-0.5"><Minus size={12} /> 0%</span>;
}

const CHART_COLORS = ["#FF5200", "#333333", "#7c3aed", "#059669"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadAll = () => {
    const h = authHeaders();
    axios.get(`${API}/admin/stats`, h).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/analytics?months=6`, h).then((r) => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/admin/contacts`, h).then((r) => setRecentContacts(r.data.slice(0, 4))).catch(() => {});
    axios.get(`${API}/admin/quotes`, h).then((r) => setRecentQuotes(r.data.slice(0, 4))).catch(() => {});
    axios.get(`${API}/admin/driver-locations`, h).then((r) => setDriverLocations(r.data)).catch(() => {});
    prefetchAdminLists();
    setLastRefresh(new Date());
  };

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kpi = analytics?.kpi;
  const statCards = stats ? [
    { icon: Truck, label: "Spedizioni", total: stats.shipments.total, highlight: stats.shipments.active, highlightLabel: "attive", color: "bg-[#FF5200]", link: "/admin/spedizioni" },
    { icon: PackageCheck, label: "Ritiri", total: stats.pickups?.total || 0, highlight: stats.pickups?.active || 0, highlightLabel: "attivi", color: "bg-[#333333]", link: "/admin/ritiri" },
    { icon: MessageSquare, label: "Contatti", total: stats.contacts.total, highlight: stats.contacts.unread, highlightLabel: "non letti", color: "bg-blue-500", link: "/admin/contatti" },
    { icon: FileText, label: "Preventivi", total: stats.quotes.total, highlight: stats.quotes.new, highlightLabel: "nuovi", color: "bg-amber-500", link: "/admin/preventivi" },
    { icon: Users, label: "Clienti", total: stats.clients.total, highlight: null, highlightLabel: "", color: "bg-emerald-500", link: "/admin/clienti" },
  ] : [];

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      {/* Refresh */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>Auto-refresh 60s</span>
          {lastRefresh && <span>| {lastRefresh.toLocaleTimeString("it-IT")}</span>}
          <button onClick={loadAll} className="p-1 hover:text-[#FF5200] transition-colors" data-testid="refresh-dashboard-btn"><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="kpi-shipments">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Spedizioni Mese</span>
              <KpiChange current={kpi.shipments.current} previous={kpi.shipments.previous} />
            </div>
            <p className="text-3xl font-bold text-[#09090B]">{kpi.shipments.current}</p>
            <p className="text-xs text-zinc-400 mt-1">vs {kpi.shipments.previous} mese prec.</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="kpi-pickups">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Ritiri Mese</span>
              <KpiChange current={kpi.pickups.current} previous={kpi.pickups.previous} />
            </div>
            <p className="text-3xl font-bold text-[#09090B]">{kpi.pickups.current}</p>
            <p className="text-xs text-zinc-400 mt-1">vs {kpi.pickups.previous} mese prec.</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="kpi-revenue">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Fatturato Mese</span>
              <KpiChange current={kpi.revenue.current} previous={kpi.revenue.previous} />
            </div>
            <p className="text-3xl font-bold text-[#FF5200]">{kpi.revenue.current.toLocaleString("it-IT", { minimumFractionDigits: 2 })} <span className="text-lg">EUR</span></p>
            <p className="text-xs text-zinc-400 mt-1">vs {kpi.revenue.previous.toLocaleString("it-IT", { minimumFractionDigits: 2 })} EUR</p>
            {kpi.revenue_warehouse?.current > 0 && (
              <p className="text-xs text-violet-600 mt-1" data-testid="kpi-revenue-warehouse">di cui magazzino: {kpi.revenue_warehouse.current.toLocaleString("it-IT", { minimumFractionDigits: 2 })} EUR</p>
            )}
            {kpi.revenue_fuel?.current > 0 && (
              <p className="text-xs text-amber-700" data-testid="kpi-revenue-fuel">di cui suppl. carburante: {kpi.revenue_fuel.current.toLocaleString("it-IT", { minimumFractionDigits: 2 })} EUR</p>
            )}
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map((card) => (
          <Link key={card.label} to={card.link}
            className="bg-white border border-zinc-200 p-4 hover:border-zinc-400 transition-colors rounded-sm"
            data-testid={`stat-card-${card.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 ${card.color} rounded-sm flex items-center justify-center`}>
                <card.icon size={18} className="text-white" />
              </div>
              <ArrowRight size={14} className="text-zinc-300" />
            </div>
            <p className="text-2xl font-bold text-[#09090B]">{card.total}</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold">{card.label}</p>
            {card.highlight !== null && card.highlight > 0 && (
              <p className="text-[10px] font-bold text-[#FF5200] mt-1">{card.highlight} {card.highlightLabel}</p>
            )}
          </Link>
        ))}
      </div>

      {/* Charts Row */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shipments/Pickups Bar Chart */}
          <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="chart-volume">
            <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B] mb-4 flex items-center gap-2">
              <Truck size={16} className="text-[#FF5200]" /> Volume Spedizioni / Ritiri
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={analytics.monthly} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} />
                <YAxis tick={{ fontSize: 11, fill: "#999" }} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
                <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="shipments" name="Spedizioni" fill="#FF5200" radius={[3, 3, 0, 0]} />
                <Bar dataKey="pickups" name="Ritiri" fill="#333333" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue Line Chart */}
          <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="chart-revenue">
            <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B] mb-4 flex items-center gap-2">
              <TrendingUp size={16} className="text-[#FF5200]" /> Andamento Fatturato (EUR)
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={analytics.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} />
                <YAxis tick={{ fontSize: 11, fill: "#999" }} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} formatter={(v) => `${v.toFixed(2)} EUR`} />
                <Legend iconType="line" wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue_shipments" name="Spedizioni" stroke="#FF5200" strokeWidth={2.5} dot={{ r: 4, fill: "#FF5200" }} />
                <Line type="monotone" dataKey="revenue_pickups" name="Ritiri" stroke="#333333" strokeWidth={2.5} dot={{ r: 4, fill: "#333" }} />
                <Line type="monotone" dataKey="revenue_warehouse" name="Magazzino" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3, fill: "#7c3aed" }} />
                <Line type="monotone" dataKey="revenue_fuel" name="Suppl. carburante" stroke="#b45309" strokeWidth={1.5} strokeDasharray="4 3" dot={{ r: 2, fill: "#b45309" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Driver Performance + Top Clients + Warehouse */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Driver Performance */}
          <div className="bg-white border border-zinc-200 rounded-sm" data-testid="driver-performance">
            <div className="px-5 py-4 border-b border-zinc-100">
              <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B] flex items-center gap-2">
                <Navigation size={16} className="text-[#FF5200]" /> Performance Autisti
              </h3>
            </div>
            <div className="divide-y divide-zinc-50">
              {analytics.drivers.length === 0 ? (
                <p className="p-5 text-zinc-400 text-sm text-center">Nessun autista</p>
              ) : analytics.drivers.map((d, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-zinc-300 w-5">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{d.name}</p>
                    <p className="text-[10px] text-zinc-400">{d.plate}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{d.delivered}<span className="text-zinc-400 font-normal">/{d.assigned}</span></p>
                    <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden mt-1">
                      <div className="h-full bg-[#FF5200] rounded-full" style={{ width: `${d.pod_ratio}%` }} />
                    </div>
                  </div>
                  <span className="text-xs font-bold text-zinc-500 w-10 text-right">{d.pod_ratio}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Clients */}
          <div className="bg-white border border-zinc-200 rounded-sm" data-testid="top-clients">
            <div className="px-5 py-4 border-b border-zinc-100">
              <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B] flex items-center gap-2">
                <Users size={16} className="text-[#FF5200]" /> Top Clienti (Volume)
              </h3>
            </div>
            <div className="divide-y divide-zinc-50">
              {analytics.top_clients.length === 0 ? (
                <p className="p-5 text-zinc-400 text-sm text-center">Nessun dato</p>
              ) : analytics.top_clients.slice(0, 8).map((c, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-zinc-300 w-5">{i + 1}.</span>
                  <p className="flex-1 text-sm font-semibold truncate">{c.name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] bg-[#FF5200]/10 text-[#FF5200] px-1.5 py-0.5 rounded font-bold">{c.shipments} SP</span>
                    <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-bold">{c.pickups} RT</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Warehouse Overview */}
          <div className="bg-white border border-zinc-200 rounded-sm" data-testid="warehouse-overview">
            <div className="px-5 py-4 border-b border-zinc-100">
              <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B] flex items-center gap-2">
                <Warehouse size={16} className="text-[#FF5200]" /> Magazzino
              </h3>
            </div>
            <div className="p-5 space-y-4">
              {[
                { label: "Articoli in Stock", value: analytics.warehouse.items, icon: "📦" },
                { label: "Movimenti Totali", value: analytics.warehouse.movements, icon: "↕" },
                { label: "Clienti Magazzino", value: analytics.warehouse.clients, icon: "👥" },
              ].map((s, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm text-zinc-600">{s.label}</span>
                  <span className="text-xl font-bold text-[#09090B]">{s.value}</span>
                </div>
              ))}
              <Link to="/admin/magazzino" className="block text-center text-xs font-bold text-[#FF5200] uppercase tracking-wide hover:underline mt-4">
                Vai al Magazzino →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Driver Tracking */}
      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="driver-tracking-section">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B] flex items-center gap-2">
            <Navigation size={16} className="text-[#FF5200]" /> Autisti in Tempo Reale
          </h3>
          <span className="text-xs text-zinc-400">{driverLocations.length} attiv{driverLocations.length === 1 ? "o" : "i"}</span>
        </div>
        {driverLocations.length === 0 ? (
          <div className="p-6 text-center">
            <MapPin size={28} className="text-zinc-300 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">Nessun autista attivo al momento</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {driverLocations.map((loc) => {
              const isRecent = loc.updated_at && (Date.now() - new Date(loc.updated_at).getTime()) < 10 * 60 * 1000;
              return (
                <div key={loc.driver_id} className="px-5 py-3 flex items-center justify-between" data-testid={`driver-location-${loc.driver_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${isRecent ? "bg-green-500 animate-pulse" : "bg-zinc-300"}`} />
                    <div>
                      <p className="font-bold text-sm">{loc.driver_name || "Autista"}</p>
                      <p className="text-[10px] text-zinc-400 flex items-center gap-1"><MapPin size={9} /> {loc.lat?.toFixed(4)}, {loc.lng?.toFixed(4)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className={`text-xs font-bold ${isRecent ? "text-green-600" : "text-zinc-400"}`}>{isRecent ? "Online" : "Offline"}</p>
                    <span className="text-[10px] text-zinc-400 flex items-center gap-0.5"><Clock size={9} /> {timeAgo(loc.updated_at)}</span>
                    <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 bg-zinc-100 rounded hover:bg-[#FF5200] hover:text-white text-zinc-500 transition-colors" data-testid={`view-driver-map-${loc.driver_id}`}>
                      <MapPin size={14} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B]">Contatti Recenti</h3>
            <Link to="/admin/contatti" className="text-xs font-bold text-[#FF5200] hover:underline uppercase tracking-wide">Vedi tutti</Link>
          </div>
          <div className="divide-y divide-zinc-50">
            {recentContacts.length === 0 ? <p className="p-5 text-zinc-400 text-sm">Nessun contatto</p> :
              recentContacts.map((c) => (
                <div key={c.id} className="px-5 py-3">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="font-bold text-sm">{c.name}</p>
                    <span className="text-[10px] text-zinc-400">{new Date(c.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{c.message}</p>
                </div>
              ))}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="font-bold text-sm uppercase tracking-wider text-[#09090B]">Preventivi Recenti</h3>
            <Link to="/admin/preventivi" className="text-xs font-bold text-[#FF5200] hover:underline uppercase tracking-wide">Vedi tutti</Link>
          </div>
          <div className="divide-y divide-zinc-50">
            {recentQuotes.length === 0 ? <p className="p-5 text-zinc-400 text-sm">Nessun preventivo</p> :
              recentQuotes.map((q) => (
                <div key={q.id} className="px-5 py-3">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="font-bold text-sm">{q.name} {q.company && `(${q.company})`}</p>
                    <StatusBadge status={q.status} />
                  </div>
                  <p className="text-xs text-zinc-500">{q.origin || "N/A"} → {q.destination || "N/A"}</p>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = { nuovo: "bg-blue-100 text-blue-700", in_lavorazione: "bg-amber-100 text-amber-700", preventivo_inviato: "bg-purple-100 text-purple-700", accettato: "bg-green-100 text-green-700", rifiutato: "bg-red-100 text-red-700" };
  const labels = { nuovo: "Nuovo", in_lavorazione: "In Lavorazione", preventivo_inviato: "Inviato", accettato: "Accettato", rifiutato: "Rifiutato" };
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-sm ${styles[status] || "bg-zinc-100 text-zinc-700"}`}>{labels[status] || status}</span>;
}
